<template>
  <div class="staff-sms-overlay" @click.self="$emit('close')">
    <div ref="dialogEl" class="staff-sms-dialog">
      <div class="staff-sms-header">
        <h3>Send SMS to staff</h3>
        <button type="button" class="staff-sms-close" @click="$emit('close')">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <div class="staff-sms-body">
        <p class="staff-sms-hint">
          Only opted-in staff with a phone number will receive the text.
          Click a name to select one person. Ctrl-click (Cmd-click on Mac) to
          select more.
        </p>

        <div v-if="loading" class="staff-sms-loading">
          <i class="fas fa-spinner fa-spin"></i> Loading staff...
        </div>

        <template v-else>
          <div class="staff-sms-list-header">
            <span>Staff</span>
            <button type="button" class="staff-sms-link" @click="selectOptedIn">
              Select opted-in
            </button>
          </div>
          <div class="staff-sms-list">
            <label
              v-for="person in staff"
              :key="person.id"
              class="staff-sms-row"
              :class="{
                disabled: !person.canReceive,
                selected: isStaffSelected(person.id),
              }"
              @click.prevent="onStaffClick(person, $event)"
            >
              <input
                type="checkbox"
                :value="person.id"
                :checked="isStaffSelected(person.id)"
                :disabled="!person.canReceive"
                @click.prevent="onStaffClick(person, $event)"
              />
              <span class="staff-sms-name">
                {{ person.displayName }}
                <span
                  v-if="unreadCountFor(person.id)"
                  class="staff-sms-badge"
                >{{ unreadCountFor(person.id) }}</span>
              </span>
              <span class="staff-sms-phone">{{ person.phoneDisplay }}</span>
              <span
                class="staff-sms-opt"
                :class="person.smsLabel"
              >{{ person.smsLabel }}</span>
            </label>
          </div>

          <label class="staff-sms-label" for="staff-sms-body">Message</label>
          <textarea
            id="staff-sms-body"
            v-model="messageBody"
            rows="5"
            placeholder="Message to selected staff..."
          ></textarea>

          <div class="staff-sms-thread-wrap">
            <div
              ref="threadPane"
              class="staff-sms-thread"
              @scroll="onThreadScroll"
            >
              <p v-if="!showThread" class="staff-sms-thread-empty">
                {{ threadPlaceholder }}
              </p>
              <p v-else-if="historyError" class="staff-sms-error">
                {{ historyError }}
              </p>
              <p
                v-else-if="!history.length && !historyLoading"
                class="staff-sms-thread-empty"
              >
                No messages yet
              </p>
              <template v-else>
                <div
                  v-for="row in history"
                  :key="row.id"
                  class="staff-sms-bubble-row"
                  :class="row.direction === 'out' ? 'outbound' : 'inbound'"
                >
                  <div class="staff-sms-bubble">
                    <div v-if="row.body" class="staff-sms-bubble-body">
                      {{ row.body }}
                    </div>
                    <template v-for="item in row.media || []" :key="item.id">
                      <img
                        v-if="smsMediaUrls[item.id]"
                        class="staff-sms-media"
                        :src="smsMediaUrls[item.id]"
                        alt="Photo"
                      />
                    </template>
                    <div class="staff-sms-bubble-stamp">
                      {{ formatStaffSmsStamp(row.createdAt) }}
                    </div>
                  </div>
                </div>
              </template>
            </div>
          </div>

          <p
            class="staff-sms-status"
            :class="{ error: !!errorText, success: !!successText }"
          >
            {{ errorText || successText || '\u00a0' }}
          </p>
        </template>
      </div>

      <div class="staff-sms-footer">
        <button
          type="button"
          class="staff-sms-btn secondary"
          @click="$emit('close')"
        >
          Cancel
        </button>
        <button
          type="button"
          class="staff-sms-btn primary"
          :disabled="sending || loading"
          @click="send"
        >
          {{ sending ? 'Sending...' : 'Send' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from '@/axiosConfig';
import {
  shouldShowStaffSmsThread,
  staffSmsThreadPlaceholder,
  mergeSmsHistory,
  formatStaffSmsStamp,
  shouldStickToBottom,
  nextStaffSmsSelection,
} from '@/utils/staffSmsThread';
import {
  loadSmsMediaObjectUrls,
  revokeSmsMediaUrls,
} from '@/utils/smsMedia';
import { makeDraggable } from '@/utils/makeDraggable';
import { smsTabIsVisible } from '@/utils/smsSeen';

export default {
  name: 'StaffSmsDialog',
  emits: ['close'],
  data() {
    return {
      loading: true,
      sending: false,
      staff: [],
      selectedIds: [],
      messageBody: '',
      errorText: '',
      successText: '',
      history: [],
      smsMediaUrls: {},
      historyError: '',
      historyLoading: false,
      historyPollError: false,
      stickToBottom: true,
      historyTimer: null,
      unreadByUserId: {},
      dragCleanup: null,
    };
  },
  computed: {
    showThread() {
      return shouldShowStaffSmsThread(this.selectedIds);
    },
    threadUserId() {
      return this.showThread ? this.selectedIds[0] : null;
    },
    threadPlaceholder() {
      return staffSmsThreadPlaceholder(this.selectedIds);
    },
  },
  watch: {
    threadUserId: {
      immediate: true,
      handler(userId) {
        this.stopHistoryPoll();
        this.clearSmsMedia();
        this.history = [];
        this.historyError = '';
        this.historyPollError = false;
        this.stickToBottom = true;
        if (userId == null) return;
        this.loadHistory();
        this.startHistoryPoll();
      },
    },
    loading(isLoading) {
      if (!isLoading) this.scrollThreadToBottom();
    },
    history() {
      this.scrollThreadToBottom();
    },
  },
  mounted() {
    this.loadStaff();
    document.addEventListener('visibilitychange', this.markStaffSmsSeen);
    this.$nextTick(() => this.initDrag());
  },
  beforeUnmount() {
    this.stopHistoryPoll();
    this.clearSmsMedia();
    document.removeEventListener('visibilitychange', this.markStaffSmsSeen);
    if (this.dragCleanup) this.dragCleanup();
  },
  methods: {
    formatStaffSmsStamp,
    isStaffSelected(userId) {
      return this.selectedIds.map(Number).includes(Number(userId));
    },
    onStaffClick(person, event) {
      const additive = !!(event && (event.ctrlKey || event.metaKey));
      this.selectedIds = nextStaffSmsSelection({
        selectedIds: this.selectedIds,
        personId: person.id,
        canReceive: person.canReceive,
        additive,
      });
    },
    initDrag() {
      const el = this.$refs.dialogEl;
      if (!el || this.dragCleanup) return;
      const hasSaved =
        localStorage.getItem('staffSmsDialogTop') &&
        localStorage.getItem('staffSmsDialogLeft');
      if (!hasSaved) {
        const width = el.offsetWidth || 560;
        const height = el.offsetHeight || 480;
        el.style.top = `${Math.max(16, (window.innerHeight - height) / 2)}px`;
        el.style.left = `${Math.max(16, (window.innerWidth - width) / 2)}px`;
      }
      this.dragCleanup = makeDraggable(el, {
        handleSelector: '.staff-sms-header',
        storageKeyPrefix: 'staffSmsDialog',
      });
    },
    unreadCountFor(userId) {
      const map = this.unreadByUserId || {};
      return Number(map[userId] || map[String(userId)] || 0);
    },
    clearSmsMedia() {
      this._smsMediaGen = (this._smsMediaGen || 0) + 1;
      revokeSmsMediaUrls(this.smsMediaUrls);
      this.smsMediaUrls = {};
    },
    async refreshSmsMedia(rows) {
      const generation = (this._smsMediaGen = (this._smsMediaGen || 0) + 1);
      const { urls, revoked } = await loadSmsMediaObjectUrls(
        rows,
        this.smsMediaUrls,
        {
          fetchBlob: (id) =>
            axios
              .get(`/api/sms/media/${id}`, { responseType: 'blob' })
              .then((res) => res.data),
          createObjectURL: (blob) => URL.createObjectURL(blob),
        },
      );
      if (this._smsMediaGen !== generation) {
        const live = new Set(Object.values(this.smsMediaUrls || {}));
        revokeSmsMediaUrls(
          Object.fromEntries(
            Object.entries(urls).filter(([, url]) => !live.has(url)),
          ),
        );
        return;
      }
      revokeSmsMediaUrls(Object.fromEntries(revoked.map((url) => [url, url])));
      this.smsMediaUrls = urls;
    },
    async markStaffSmsSeen() {
      if (!smsTabIsVisible(document)) return;
      try {
        await axios.post('/api/staff-sms/seen');
      } catch (seenErr) {
        console.error('Failed to mark staff SMS seen', seenErr);
      }
    },
    async loadStaff() {
      this.loading = true;
      this.errorText = '';
      try {
        const { data } = await axios.get('/api/staff-sms');
        const staff = Array.isArray(data) ? data : data.staff || [];
        this.staff = staff;
        this.unreadByUserId = Array.isArray(data)
          ? {}
          : data.unreadByUserId || {};
        const latestId = Array.isArray(data) ? null : data.latestReplyUserId;
        if (
          latestId != null &&
          staff.some((person) => Number(person.id) === Number(latestId))
        ) {
          this.selectedIds = [Number(latestId)];
        }
        await this.markStaffSmsSeen();
      } catch (err) {
        this.errorText = err.response?.data?.error || 'Failed to load staff';
      } finally {
        this.loading = false;
        this.stickToBottom = true;
        this.scrollThreadToBottom();
      }
    },
    selectOptedIn() {
      this.selectedIds = this.staff
        .filter((person) => person.canReceive)
        .map((person) => person.id);
    },
    async send() {
      this.errorText = '';
      this.successText = '';
      this.sending = true;
      const keepSelected = this.selectedIds.length === 1;
      const keptIds = keepSelected ? [...this.selectedIds] : [];
      try {
        const { data } = await axios.post('/api/staff-sms', {
          userIds: this.selectedIds,
          body: this.messageBody,
        });
        const skipped =
          data.skipped > 0 ? ` (${data.skipped} skipped)` : '';
        this.successText = `Sent ${data.sent}${skipped}.`;
        this.messageBody = '';
        if (keepSelected) {
          this.selectedIds = keptIds;
          this.stickToBottom = true;
          await this.loadHistory();
        } else {
          this.selectedIds = [];
        }
      } catch (err) {
        this.errorText = err.response?.data?.error || 'Failed to send SMS';
      } finally {
        this.sending = false;
      }
    },
    startHistoryPoll() {
      this.stopHistoryPoll();
      this.historyTimer = setInterval(() => {
        this.loadHistory({ silent: true });
      }, 5000);
    },
    stopHistoryPoll() {
      if (this.historyTimer) {
        clearInterval(this.historyTimer);
        this.historyTimer = null;
      }
    },
    onThreadScroll() {
      const pane = this.$refs.threadPane;
      if (!pane) return;
      this.stickToBottom = shouldStickToBottom({
        scrollTop: pane.scrollTop,
        clientHeight: pane.clientHeight,
        scrollHeight: pane.scrollHeight,
      });
    },
    scrollThreadToBottom() {
      const apply = () => {
        const pane = this.$refs.threadPane;
        if (!pane || !this.stickToBottom) return false;
        pane.scrollTop = pane.scrollHeight;
        return true;
      };
      this.$nextTick(() => {
        if (apply()) return;
        this.$nextTick(apply);
      });
    },
    async loadHistory({ silent = false } = {}) {
      const userId = this.threadUserId;
      if (userId == null) return;
      if (!silent) {
        this.historyLoading = true;
        this.historyError = '';
      }
      try {
        const { data } = await axios.get(
          `/api/staff-sms/${userId}/messages`,
        );
        if (this.threadUserId !== userId) return;
        const incoming = Array.isArray(data) ? data : [];
        this.history = mergeSmsHistory(this.history, incoming);
        this.refreshSmsMedia(this.history);
        this.historyError = '';
        this.historyPollError = false;
        this.scrollThreadToBottom();
      } catch (err) {
        const message =
          err.response?.data?.error || 'Failed to load SMS history';
        if (silent) {
          if (!this.historyPollError && !this.history.length) {
            this.historyError = message;
          }
          this.historyPollError = true;
        } else {
          this.historyError = message;
        }
      } finally {
        this.historyLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.staff-sms-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  z-index: 4000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.staff-sms-dialog {
  position: fixed;
  width: 560px;
  max-width: calc(100vw - 32px);
  max-height: calc(100vh - 32px);
  background: #f8f8f8;
  border: 1px solid #888;
  border-radius: 8px;
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.staff-sms-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: #6e5875;
  color: #fff;
  cursor: move;
}

.staff-sms-header h3 {
  margin: 0;
  font-size: 1.05rem;
}

.staff-sms-close {
  border: none;
  background: transparent;
  color: #fff;
  cursor: pointer;
  font-size: 1.1rem;
}

.staff-sms-body {
  padding: 16px;
  overflow-y: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.staff-sms-body::-webkit-scrollbar {
  width: 0;
  height: 0;
}

.staff-sms-hint {
  margin: 0 0 12px;
  font-size: 0.85rem;
  color: #555;
  line-height: 1.4;
}

.staff-sms-loading {
  text-align: center;
  padding: 24px 0;
  color: #666;
}

.staff-sms-list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 600;
  font-size: 0.9rem;
  margin-bottom: 6px;
}

.staff-sms-link {
  border: none;
  background: transparent;
  color: #5f4b8b;
  cursor: pointer;
  font-size: 0.85rem;
  text-decoration: underline;
  padding: 0;
}

.staff-sms-list {
  max-height: 220px;
  overflow-y: auto;
  border: 1px solid #ccc;
  background: #fff;
  margin-bottom: 12px;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.staff-sms-list::-webkit-scrollbar {
  width: 0;
  height: 0;
}

.staff-sms-row {
  display: grid;
  grid-template-columns: auto 1fr auto auto;
  gap: 8px;
  align-items: center;
  padding: 6px 8px;
  font-size: 0.9rem;
  border-bottom: 1px solid #eee;
  cursor: pointer;
}

.staff-sms-row.selected {
  background: #efe8f2;
}

.staff-sms-row.disabled {
  color: #888;
  cursor: default;
}

.staff-sms-name {
  font-weight: 600;
}

.staff-sms-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  padding: 0;
  margin-left: 6px;
  border-radius: 50%;
  background: #c0392b;
  color: #fff;
  font-size: 0.65rem;
  font-weight: 700;
  line-height: 1;
  vertical-align: middle;
}

.staff-sms-phone,
.staff-sms-opt {
  font-variant-numeric: tabular-nums;
  font-size: 0.85rem;
}

.staff-sms-opt.opt-in {
  color: #2d6a4f;
}

.staff-sms-opt.opt-out,
.staff-sms-opt.- {
  color: #888;
}

.staff-sms-label {
  display: block;
  font-weight: 600;
  font-size: 0.9rem;
  margin-bottom: 4px;
}

.staff-sms-body textarea {
  width: 100%;
  box-sizing: border-box;
  padding: 8px;
  font-size: 0.95rem;
  resize: vertical;
}

.staff-sms-thread-wrap {
  margin-top: 10px;
}

.staff-sms-thread {
  height: 220px;
  box-sizing: border-box;
  overflow-y: auto;
  border: 1px solid #ccc;
  background: #ececec;
  padding: 8px;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.staff-sms-thread::-webkit-scrollbar {
  width: 0;
  height: 0;
}

.staff-sms-thread-empty {
  margin: 0;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: #666;
  font-size: 0.85rem;
}

.staff-sms-bubble-row {
  display: flex;
  margin-bottom: 8px;
}

.staff-sms-bubble-row.outbound {
  justify-content: flex-end;
}

.staff-sms-bubble-row.inbound {
  justify-content: flex-start;
}

.staff-sms-bubble {
  max-width: 80%;
}

.staff-sms-media {
  display: block;
  width: 100%;
  height: auto;
  margin-top: 4px;
  object-fit: contain;
  border-radius: 8px;
}

.staff-sms-bubble-body {
  padding: 8px 10px;
  border-radius: 10px;
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 0.9rem;
  line-height: 1.35;
}

.staff-sms-bubble-row.outbound .staff-sms-bubble-body {
  background: #6e5875;
  color: #fff;
}

.staff-sms-bubble-row.inbound .staff-sms-bubble-body {
  background: #fff;
  color: #222;
  border: 1px solid #ddd;
}

.staff-sms-bubble-stamp {
  margin-top: 3px;
  font-size: 0.75rem;
  color: #666;
}

.staff-sms-bubble-row.outbound .staff-sms-bubble-stamp {
  text-align: right;
}

.staff-sms-error {
  color: #a33;
  margin: 8px 0 0;
}

.staff-sms-status {
  margin: 8px 0 0;
  min-height: 1.25em;
  font-size: 0.9rem;
}

.staff-sms-status.error {
  color: #a33;
}

.staff-sms-status.success {
  color: #2d6a4f;
}

.staff-sms-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 16px;
  border-top: 1px solid #ddd;
}

.staff-sms-btn {
  padding: 6px 14px;
  cursor: pointer;
}

.staff-sms-btn.primary {
  background: #6e5875;
  color: #fff;
  border: 1px solid #5a4760;
}

.staff-sms-btn.secondary {
  background: #eee;
  border: 1px solid #bbb;
}

.staff-sms-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
